import React, { useEffect, useState } from "react";
import {
    getInquirySourceList,
    getVerticalList,
    getContactById,
    updateContact,
    getResponsibleUsers,
    convertToFollowup,
} from "../api/contactService";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { getClientSegmentByVertical, getResponsibleUsersByStage } from "../../sales/inquiry/api/inquiry.api";
import Swal from "sweetalert2";
import { useAuth } from "../../../context/AuthContext";
import PortalSelect, { type PortalSelectOption } from "../../../components/common/PortalSelect";


const getVerticalKey = (name: string) => {
    const n = name.toLowerCase().trim();

    if (n.includes("glazing")) return "glazing";
    if (n.includes("hunter")) return "hunter_douglas";
    if (n.includes("lixil")) return "lixil";

    return "";
};

const ContactEdit = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { id } = useParams();

    const canConvertToFollowup =
        user?.role === "Admin" || user?.role === "Manager";
    const [usersByVertical, setUsersByVertical] = useState<any>({});

    const [form, setForm] = useState<any>({
        name: "",
        mobile_no: "",
        inquiry_source: "",

        email: "",
        company_name: "",
        designation: "",
        address: "",
        city: "",
        state: "",
        contact_persons: [
            { name: "", mobile: "", email: "", designation: "" }
        ],
        interested_verticals: [],
        verticals: [], // ✅ ADD THIS

    });

    const [inquirySources, setInquirySources] = useState<any[]>([]);
    const [verticals, setVerticals] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [isConverted, setIsConverted] = useState(false);
    const [fieldErrors, setFieldErrors] = useState<any>({});
    /* ================= LOAD MASTER ================= */
    useEffect(() => {
        loadMasters();
    }, []);

    const loadMasters = async () => {
        try {
            const [sourceRes, verticalRes] = await Promise.all([
                getInquirySourceList(),
                getVerticalList(),
            ]);

            setInquirySources(sourceRes.data?.results || []);
            setVerticals(verticalRes.data?.results || []);
        } catch {
            toast.error("Failed to load master data");
        }
    };

    /* ================= LOAD CONTACT ================= */
    useEffect(() => {
        if (id && verticals.length > 0) {
            loadContact();
        }
    }, [id, verticals]);
    const handleVerticalToggle = async (verticalId: number) => {
        const exists = form.verticals.find(
            (v: any) => v.vertical_id === verticalId
        );

        if (exists) {
            setForm({
                ...form,
                verticals: form.verticals.filter(
                    (v: any) => v.vertical_id !== verticalId
                ),
            });
            return;
        }

        try {
            let segments: any[] = [];

            if (!usersByVertical[verticalId]) {
                const userRes = await getResponsibleUsersByStage(verticalId);

                setUsersByVertical((prev: any) => ({
                    ...prev,
                    [verticalId]: userRes.data?.data || [],
                }));
            }

            const segmentRes = await getClientSegmentByVertical(verticalId);
            segments = segmentRes.data?.results || [];

            setForm((prev: any) => ({
                ...prev,
                verticals: [
                    ...prev.verticals,
                    {
                        vertical_id: verticalId,
                        assigned_to: null,
                        client_segment: null,
                        segments,
                    },
                ],
            }));
        } catch {
            toast.error("Failed to load vertical data");
        }
    };
    const getVerticalId = (key: string) => {
        const v = verticals.find((v: any) =>
            v.name.toLowerCase().includes(key)
        );
        return v?.id;
    };

    const loadContact = async () => {
        try {
            const res = await getContactById(Number(id));
            const data = res.data;

            const verticalData: any[] = [];

            if (data.is_glazing) {
                verticalData.push({
                    vertical_id: getVerticalId("glazing"),
                    assigned_to: data.glazing_assigned_to,
                    client_segment: data.glazing_segment,
                    segments: [],
                });
            }

            if (data.is_lixil) {
                verticalData.push({
                    vertical_id: getVerticalId("lixil"),
                    assigned_to: data.lixil_assigned_to,
                    client_segment: data.lixil_segment,
                    segments: [],
                });
            }

            if (data.is_hunter_douglas) {
                verticalData.push({
                    vertical_id: getVerticalId("hunter"),
                    assigned_to: data.hunter_douglas_assigned_to,
                    client_segment: data.hunter_douglas_segment,
                    segments: [],
                });
            }
            const contactPersons = (data.contact_persons || []).map((c: any) => ({
                name: c.name || "",
                mobile: c.mobile || "",
                email: c.email || "",
                designation: c.designation || ""
            }));
            setForm({
                name: data.name || "",
                mobile_no: data.phone || "",
                inquiry_source: data.source || "",

                email: data.email || "",
                company_name: data.company_name || "",
                designation: data.designation || "",
                address: data.address || "",
                city: data.city || "",
                state: data.state || "",
                contact_persons: contactPersons.length
                    ? contactPersons
                    : [{ name: "", mobile: "", email: "", designation: "" }],
                interested_verticals: [
                    data.is_glazing ? getVerticalId("glazing") : null,
                    data.is_lixil ? getVerticalId("lixil") : null,
                    data.is_hunter_douglas ? getVerticalId("hunter") : null,
                ].filter(Boolean),

                verticals: verticalData, // ✅ IMPORTANT
            });
            verticalData.forEach((v) => {
                if (!v.vertical_id) return;

                // 🔹 Load segments
                getClientSegmentByVertical(v.vertical_id).then((segRes) => {
                    setForm((prev: any) => ({
                        ...prev,
                        verticals: prev.verticals.map((item: any) =>
                            item.vertical_id === v.vertical_id
                                ? { ...item, segments: segRes.data?.results || [] }
                                : item
                        ),
                    }));
                });

                // 🔹 Load users
                getResponsibleUsers({ vertical_id: v.vertical_id }).then((userRes) => {
                    setUsersByVertical((prev: any) => ({
                        ...prev,
                        [v.vertical_id]: userRes.data?.data || [],
                    }));
                });
            });
        } catch {
            toast.error("Failed to load contact");
        }
    };

    /* ================= CHECKBOX ================= */

    /* ================= VALIDATION ================= */
    const validate = () => {
        const errors: any = {};

        if (!form.name) {
            errors.name = "Contact Name is required";
        }

        if (!form.mobile_no) {
            errors.mobile_no = "Mobile is required";
        } else if (!/^[6-9]\d{9}$/.test(form.mobile_no)) {
            errors.mobile_no = "Enter valid 10-digit mobile number";
        }

        if (!form.inquiry_source) {
            errors.inquiry_source = "Source is required";
        }

        if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) {
            errors.email = "Enter valid email";
        }

        // ✅ Contact details optional
        form.contact_persons.forEach((c: any, i: number) => {

            if (c.mobile && !/^[6-9]\d{9}$/.test(c.mobile)) {
                errors[`contact_mobile_${i}`] = "Valid mobile required";
            }

            if (c.email && !/^\S+@\S+\.\S+$/.test(c.email)) {
                errors[`contact_email_${i}`] = "Invalid email";
            }

        });

        return errors;
    };
    const handleContactChange = (index: number, field: string, value: string) => {
        const updated = [...form.contact_persons];
        updated[index][field] = value;

        setForm({ ...form, contact_persons: updated });
    };

    const addContact = () => {
        setForm({
            ...form,
            contact_persons: [
                ...form.contact_persons,
                { name: "", mobile: "", email: "", designation: "" }
            ]
        });
    };

    const removeContact = (index: number) => {
        if (index === 0) return;

        setForm({
            ...form,
            contact_persons: form.contact_persons.filter((_: any, i: number) => i !== index)
        });
    };

    /* ================= SUBMIT ================= */
    const handleSubmit = async () => {
        setFieldErrors({});
        const errors = validate();

        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            toast.error("Please fix highlighted fields");
            return;
        }

        try {
            setLoading(true);

            const payload: any = {
                name: form.name,
                phone: form.mobile_no,
                source: form.inquiry_source,

                email: form.email || null,
                company_name: form.company_name || null,
                designation: form.designation || null,
                address: form.address || null,
                city: form.city || null,
                state: form.state || null,

                is_glazing: false,
                is_lixil: false,
                is_hunter_douglas: false,

                glazing_assigned_to: null,
                lixil_assigned_to: null,
                hunter_douglas_assigned_to: null,

                glazing_segment: null,
                lixil_segment: null,
                hunter_douglas_segment: null,
            };
            payload.contact_persons = form.contact_persons.map((c: any) => ({
                name: c.name,
                mobile: c.mobile,
                email: c.email || null,
                designation: c.designation
            }));
            form.verticals.forEach((v: any) => {
                const vertical = verticals.find((x: any) => x.id === v.vertical_id);
                const key = getVerticalKey(vertical?.name);

                if (!key) return;

                payload[`is_${key}`] = true;
                payload[`${key}_assigned_to`] = v.assigned_to;
                payload[`${key}_segment`] = v.client_segment;
            });

            await updateContact(Number(id), payload);

            toast.success("Contact updated successfully");
            navigate("/masters/contact");
        } catch (err: any) {
            console.error(err);

            const data = err?.response?.data || {};

            // 🔥 STORE ALL FIELD ERRORS
            setFieldErrors(data);

            // optional toast (generic)
            toast.error("Please fix highlighted fields");
        } finally {
            setLoading(false);
        }
    };

    const handleConvertFollowup = async () => {
        if (!canConvertToFollowup) {
            toast.error("Only Admin or Manager can convert contact to followup");
            return;
        }

        if (!id) {
            toast.error("Invalid contact ID"); // keep toast
            return;
        }

        // ✅ SweetAlert only here
        const result = await Swal.fire({
            title: "Are you sure?",
            text: "Convert this contact to followup?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, convert",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#198754", // Bootstrap success color
            cancelButtonColor: "#6c757d",
        });

        if (!result.isConfirmed) return;

        try {
            setLoading(true);

            const res = await convertToFollowup(Number(id));

            // ✅ SweetAlert success
            await Swal.fire({
                icon: "success",
                title: "Converted!",
                text: res?.data?.message || "Contact converted successfully",
                timer: 1500,
                showConfirmButton: false,
                allowOutsideClick: false,
            });

            setIsConverted(true);
            navigate("/masters/contact");

        } catch (err: any) {
            console.error(err);

            const raw = err?.response?.data || {};

            setFieldErrors(raw); // 🔥 store full error object

            toast.error("Please fix highlighted fields");
        } finally {
            setLoading(false);
        }
    };

    const getErrorForVertical = (verticalName: string) => {
        const key = getVerticalKey(verticalName);
        return fieldErrors?.[`${key}_assigned_to`]?.[0];
    };
    return (
        <div className="card common-form-card p-4">
            {/* BASIC */}
            <Section title="Basic Info">
                <SelectField
                    label="Source*"
                    options={inquirySources}
                    value={form.inquiry_source}
                    error={fieldErrors.inquiry_source}
                    onChange={(val: any) => {
                        setForm({ ...form, inquiry_source: val });

                        setFieldErrors((prev: any) => {
                            const updated = { ...prev };
                            delete updated.inquiry_source;
                            return updated;
                        });
                    }}
                />

                <Input
                    label="Contact Name*"
                    value={form.name}
                    error={fieldErrors.name}
                    onChange={(e: any) => {
                        setForm({ ...form, name: e.target.value });

                        setFieldErrors((prev: any) => {
                            const updated = { ...prev };
                            delete updated.name;
                            return updated;
                        });
                    }}
                />

                <Input
                    label="Mobile*"
                    value={form.mobile_no}
                    error={fieldErrors.mobile_no}
                    onChange={(e: any) => {
                        setForm({ ...form, mobile_no: e.target.value });

                        setFieldErrors((prev: any) => {
                            const updated = { ...prev };
                            delete updated.mobile_no;
                            return updated;
                        });
                    }}
                />
            </Section>

            {/* OTHER */}
            <Section title="Other Details">
                <Input
                    label="Email"
                    value={form.email}
                    error={fieldErrors.email}
                    onChange={(e: any) => {
                        setForm({ ...form, email: e.target.value });

                        setFieldErrors((prev: any) => {
                            const updated = { ...prev };
                            delete updated.email;
                            return updated;
                        });
                    }}
                />

                <Input
                    label="Company Name"
                    value={form.company_name}
                    onChange={(e: any) =>
                        setForm({ ...form, company_name: e.target.value })
                    }
                />

                <Input
                    label="Designation"
                    value={form.designation}
                    onChange={(e: any) =>
                        setForm({ ...form, designation: e.target.value })
                    }
                />

                <Input
                    label="Address"
                    value={form.address}
                    onChange={(e: any) =>
                        setForm({ ...form, address: e.target.value })
                    }
                />

                <Input
                    label="City"
                    value={form.city}
                    onChange={(e: any) =>
                        setForm({ ...form, city: e.target.value })
                    }
                />

                <Input
                    label="State"
                    value={form.state}
                    onChange={(e: any) =>
                        setForm({ ...form, state: e.target.value })
                    }
                />
            </Section>
            {/* <div className="form-section mt-4">
                <div className="form-section-header bg-primary text-white d-flex justify-content-between align-items-center">
                    <span>Contact Details</span>
                    <button className="btn btn-sm btn-light" onClick={addContact}>
                        Add Contact
                    </button>
                </div>

                {form.contact_persons.map((contact: any, index: number) => (
                    <div className="row g-3 align-items-center  p-3" key={index}>

                        <div className="col-md-3">
                            <input
                                className="form-control"
                                placeholder="Contact Name *"
                                value={contact.name}
                                onChange={(e) =>
                                    handleContactChange(index, "name", e.target.value)
                                }
                            />
                        </div>

                        <div className="col-md-3">
                            <input
                                className="form-control"
                                placeholder="Contact Number *"
                                value={contact.mobile}
                                onChange={(e) =>
                                    handleContactChange(index, "mobile", e.target.value)
                                }
                            />
                        </div>

                        <div className="col-md-3">
                            <input
                                className="form-control"
                                placeholder="Email ID"
                                value={contact.email}
                                onChange={(e) =>
                                    handleContactChange(index, "email", e.target.value)
                                }
                            />
                        </div>

                        <div className="col-md-2">
                            <input
                                className="form-control"
                                placeholder="Designation *"
                                value={contact.designation}
                                onChange={(e) =>
                                    handleContactChange(index, "designation", e.target.value)
                                }
                            />
                        </div>

                        <div className="col-md-1 text-end">
                            {index !== 0 && (
                                <button
                                    className="btn btn-outline-danger btn-sm"
                                    onClick={() => removeContact(index)}
                                >
                                    ✕
                                </button>
                            )}
                        </div>

                    </div>
                ))}
            </div> */}
            {/* INTERESTED VERTICALS */}
            <Section title="Vertical(s)">
                <div className="row">
                    {verticals.map((v: any) => (
                        <div className="col-md-3 mb-2" key={v.id}>
                            <label className="d-flex gap-2">
                                <input
                                    type="checkbox"
                                    checked={!!form.verticals.find((x: any) => x.vertical_id === v.id)}
                                    onChange={() => handleVerticalToggle(v.id)}
                                />
                                {v.name}
                            </label>
                        </div>
                    ))}
                </div>
            </Section>

            {form.verticals.length > 0 && (
                <Section title="Vertical Details">
                    <div className="row fw-bold mb-2">
                        <div className="col-md-4">Vertical</div>
                        <div className="col-md-4">Client Segment</div>
                        <div className="col-md-4">Assigned To *</div>
                    </div>

                    {form.verticals.map((vItem: any) => {
                        const v = verticals.find((x: any) => x.id === vItem.vertical_id);
                        if (!v) return null;

                        const segmentOptions: PortalSelectOption[] = (vItem.segments || []).map((s: any) => ({
                            value: s.id,
                            label: s.name,
                        }));

                        const assignedToOptions: PortalSelectOption[] = (usersByVertical[v.id] || []).map((u: any) => ({
                            value: u.id,
                            label: u.name || u.username,
                        }));

                        return (
                            <div className="row mb-2" key={v.id}>

                                <div className="col-md-4">
                                    {v.name}
                                </div>

                                <div className="col-md-4">
                                    <PortalSelect
                                        placeholder="Select Segment"
                                        options={segmentOptions}
                                        value={
                                            segmentOptions.find(
                                                (opt) => opt.value === vItem.client_segment
                                            ) || null
                                        }
                                        onChange={(selected) => {
                                            const value = selected?.value != null
                                                ? Number(selected.value)
                                                : null;

                                            setForm((prev: any) => ({
                                                ...prev,
                                                verticals: prev.verticals.map((item: any) =>
                                                    item.vertical_id === v.id
                                                        ? { ...item, client_segment: value }
                                                        : item
                                                ),
                                            }));
                                        }}
                                    />
                                </div>

                                <div className="col-md-4">
                                    <PortalSelect
                                        placeholder="Select User"
                                        isInvalid={!!fieldErrors?.[`${getVerticalKey(v.name)}_assigned_to`]}
                                        options={assignedToOptions}
                                        value={
                                            assignedToOptions.find(
                                                (opt) => opt.value === vItem.assigned_to
                                            ) || null
                                        }
                                        onChange={(selected) => {
                                            const value = selected?.value != null
                                                ? Number(selected.value)
                                                : null;

                                            setForm((prev: any) => ({
                                                ...prev,
                                                verticals: prev.verticals.map((item: any) =>
                                                    item.vertical_id === v.id
                                                        ? { ...item, assigned_to: value }
                                                        : item
                                                ),
                                            }));

                                            setFieldErrors((prev: any) => {
                                                const updated = { ...prev };
                                                delete updated[`${getVerticalKey(v.name)}_assigned_to`];
                                                return updated;
                                            });
                                        }}
                                    />

                                    {fieldErrors?.[`${getVerticalKey(v.name)}_assigned_to`] && (
                                        <small className="text-danger">
                                            {fieldErrors[`${getVerticalKey(v.name)}_assigned_to`][0]}
                                        </small>
                                    )}
                                </div>

                            </div>
                        );
                    })}
                </Section>
            )}

            {/* SUBMIT */}
            <div className="text-end mt-3 d-flex gap-2 justify-content-end contact-edit-actions">

                <button
                    className="btn btn-outline-secondary"
                    onClick={() => navigate("/masters/contact")}
                    disabled={loading}
                >
                    Cancel
                </button>

                {canConvertToFollowup && (
                    <button
                        className="btn btn-success"
                        onClick={handleConvertFollowup}
                        disabled={loading || isConverted}
                    >
                        {isConverted ? "Converted" : "Convert to Followup"}
                    </button>
                )}

                {/* UPDATE BUTTON */}
                <button
                    className="btn btn-primary"
                    onClick={handleSubmit}
                    disabled={loading}
                >
                    {loading ? "Updating..." : "Update"}
                </button>
            </div>
        </div>
    );
};

export default ContactEdit;

/* ================= UI ================= */

const Section = ({ title, children }: any) => (
    <div className="form-section">
        <div className="form-section-header bg-primary text-white">
            {title}
        </div>
        <div className="row g-3 p-3">{children}</div>
    </div>
);

const Input = ({ label, error, ...props }: any) => (
    <div className="col-md-4">
        <label className="form-label">{label}</label>
        <input
            className={`form-control ${error ? "is-invalid" : ""}`}
            {...props}
        />
        {error && <small className="text-danger">{error}</small>}
    </div>
);

const SelectField = ({ label, options = [], value, onChange, error }: any) => (
    <div className="col-md-4">
        <label className="form-label">{label}</label>
        <select
            className={`form-select ${error ? "is-invalid" : ""}`}
            value={value ?? ""}
            onChange={(e) => {
                const nextValue = e.target.value ? Number(e.target.value) : "";
                onChange(nextValue);
            }}
        >
            <option value="">Select {label.replace("*", "").trim()}</option>
            {options.map((o: any) => (
                <option key={o.id} value={o.id}>
                    {o.name}
                </option>
            ))}
        </select>
        {error && <small className="text-danger">{error}</small>}
    </div>
);

